// ─────────────────────────────────────────────
//  src/pages/IntelligenceBoard.js
//  Market Intelligence Board — VeeraStockIQ
//
//  Features:
//  • Drag-and-drop cards between columns (HTML5 native, no extra lib needed)
//  • Add / rename / delete columns
//  • Add / edit / delete cards with full modal
//  • Asset type tagging + priority/sentiment coloring
//  • Smart stock symbol linking (→ /stock/:symbol)
//  • Basic Markdown rendering (bold, lists, checkboxes)
//  • Filter by asset type & sentiment
//  • Full-text search
//  • localStorage persistence
// ─────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./IntelligenceBoard.css";

// ── Smart stock symbol detection ──────────────
// Instead of a fixed whitelist, we detect ANY title that looks like
// a valid NSE/BSE symbol: 2-20 uppercase letters (with optional & or -)
// AND the asset type is set to "Stock" on the card.
// For non-stock cards, we still check against a broad known list so
// sectors/notes aren't accidentally linked.
//
// Rules for auto-linking:
//   1. Card assetType === "stock"  →  always link (user said it's a stock)
//   2. Title matches /^[A-Z][A-Z0-9&\-]{1,19}$/ AND has no spaces → link
//   3. Common English words (IT, AT, IN, LT, etc.) are excluded
const COMMON_WORDS = new Set([
  "AT","BE","BY","DO","GO","HE","IF","IN","IS","IT","ME","MY","NO","OF",
  "ON","OR","SO","TO","UP","US","WE","AND","ARE","BUT","CAN","DID","FOR",
  "GET","GOT","HAS","HAD","HIM","HIS","HOW","LET","MAY","NEW","NOT","NOW",
  "OLD","ONE","OUR","OUT","OWN","SAY","SHE","THE","TOO","TWO","USE","WAS",
  "WAY","WHO","WHY","YES","YET","YOU","FUND","BOND","GOLD","RATE","NOTE",
  "SELL","HOLD","HIGH","CASH","DEBT","RISK","PLAN","BANK","AUTO","TECH",
]);

function isStockSymbol(title, assetType) {
  const upper = title.trim().toUpperCase();
  // If user explicitly tagged as stock, always link
  if (assetType === "stock") return true;
  // Must look like a ticker: 2–20 chars, uppercase letters/digits/&/-
  if (!/^[A-Z][A-Z0-9&\-]{1,19}$/.test(upper)) return false;
  // No spaces allowed in a ticker
  if (title.includes(" ")) return false;
  // Exclude common English words to avoid false links
  if (COMMON_WORDS.has(upper)) return false;
  return true;
}

// ── Sector live data map (matches SectorHeatmap) ──
const SECTOR_KEYS = {
  "IT": "CNXIT", "NIFTY IT": "CNXIT",
  "BANK": "NSEBANK", "NIFTY BANK": "NSEBANK",
  "PHARMA": "CNXPHARMA", "FMCG": "CNXFMCG",
  "AUTO": "CNXAUTO", "METAL": "CNXMETAL",
  "REALTY": "CNXREALTY", "ENERGY": "CNXENERGY",
};

// ── ID generator ──────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 10);

// ── Default board state ───────────────────────
const DEFAULT_BOARD = {
  columns: [
    { id: uid(), title: "Sectors to Watch",        color: "#a78bfa" },
    { id: uid(), title: "High Conviction Stocks",  color: "#22c55e" },
    { id: uid(), title: "MF / ETF Research",       color: "#fbbf24" },
    { id: uid(), title: "Bonds / Fixed Income",    color: "#60a5fa" },
  ],
  cards: {},
};

// ── Markdown renderer ─────────────────────────
// Line-by-line processing — checkboxes checked BEFORE generic list rule
function renderMarkdown(text) {
  if (!text) return "";
  const lines = text.split("\n");
  const out   = [];
  let inList  = false;

  const inline = (s) => s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g,     "<em>$1</em>")
    .replace(/`(.+?)`/g,       '<code style="background:rgba(255,255,255,0.08);padding:1px 5px;border-radius:3px;font-family:var(--siq-mono);font-size:0.85em">$1</code>');

  for (const raw of lines) {
    // Checked checkbox
    if (/^- \[x\] /i.test(raw)) {
      if (!inList) { out.push('<ul style="padding-left:0;margin:4px 0">'); inList = true; }
      out.push(`<li style="list-style:none;display:flex;gap:6px;margin:2px 0"><span style="color:#22c55e">☑</span><s style="opacity:0.6">${inline(raw.replace(/^- \[x\] /i, ""))}</s></li>`);
      continue;
    }
    // Unchecked checkbox
    if (/^- \[ \] /.test(raw)) {
      if (!inList) { out.push('<ul style="padding-left:0;margin:4px 0">'); inList = true; }
      out.push(`<li style="list-style:none;display:flex;gap:6px;margin:2px 0"><span style="opacity:0.4">☐</span>${inline(raw.replace(/^- \[ \] /, ""))}</li>`);
      continue;
    }
    // Unordered list
    if (/^[-*] /.test(raw)) {
      if (!inList) { out.push('<ul style="padding-left:16px;margin:4px 0">'); inList = true; }
      out.push(`<li style="margin:2px 0">${inline(raw.replace(/^[-*] /, ""))}</li>`);
      continue;
    }
    // Close list
    if (inList) { out.push("</ul>"); inList = false; }
    // Blank line
    if (raw.trim() === "") { out.push('<div style="height:5px"></div>'); continue; }
    // Normal line
    out.push(`<p style="margin:0 0 3px;line-height:1.55">${inline(raw)}</p>`);
  }
  if (inList) out.push("</ul>");
  return out.join("");
}

// ── Asset type options ────────────────────────
const ASSET_TYPES = [
  { value: "stock",  label: "Stock"   },
  { value: "sector", label: "Sector"  },
  { value: "mf",     label: "MF"      },
  { value: "etf",    label: "ETF"     },
  { value: "bond",   label: "Bond"    },
  { value: "note",   label: "Note"    },
];

const PRIORITY_OPTS = [
  { value: "bull", label: "🟢 Bullish",  cls: "selected-bull" },
  { value: "bear", label: "🔴 Bearish",  cls: "selected-bear" },
  { value: "neut", label: "🟡 Neutral",  cls: "selected-neut" },
  { value: "none", label: "⚪ None",     cls: "selected-none" },
];

// ── Column colors ─────────────────────────────
const COL_COLORS = ["#a78bfa","#22c55e","#fbbf24","#60a5fa","#f87171","#34d399","#fb923c","#e879f9"];

// ── Storage ───────────────────────────────────
const STORAGE_KEY = "siq_intel_board_v2";

function loadBoard() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return DEFAULT_BOARD;
}

function saveBoard(board) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(board)); } catch {}
}

// ── Smart title: auto-link stock symbols ──────
function SmartTitle({ title, assetType, navigate }) {
  const upper = title.trim().toUpperCase();
  if (isStockSymbol(title, assetType)) {
    return (
      <span
        style={{ display: "flex", alignItems: "center", gap: 5 }}
        title={`Click to view ${upper} on StockIQ`}
      >
        <a
          href={`/stock/${upper}`}
          onClick={(e) => { e.stopPropagation(); navigate(`/stock/${upper}`); e.preventDefault(); }}
        >
          {title}
        </a>
        <span style={{ fontSize: "0.6rem", opacity: 0.5, fontFamily: "var(--siq-mono)" }}>↗</span>
      </span>
    );
  }
  return <span>{title}</span>;
}

// ── Card Component ────────────────────────────
function BoardCard({ card, onEdit, onDelete, onDragStart, onDragEnd }) {
  const navigate = useNavigate();
  const ts = new Date(card.updatedAt).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });

  const typeLabel = ASSET_TYPES.find(t => t.value === card.assetType)?.label || "Note";

  return (
    <div
      className={`ib-card ${card.priority || "none"}`}
      draggable
      onDragStart={(e) => onDragStart(e, card.id)}
      onDragEnd={onDragEnd}
      onClick={() => onEdit(card)}
    >
      <div className="ib-card-top">
        <div className="ib-card-title-wrap">
          <div className="ib-card-title">
            <SmartTitle title={card.title} assetType={card.assetType} navigate={navigate} />
          </div>
        </div>
        <span className={`ib-card-type-badge type-${card.assetType || "note"}`}>
          {typeLabel}
        </span>
      </div>

      {card.description && (
        <div
          className="ib-card-desc"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(card.description) }}
        />
      )}

      <div className="ib-card-footer">
        <span className="ib-card-ts">{ts}</span>
        <div className="ib-card-actions">
          <button
            className="ib-card-action-btn"
            title="Edit"
            onClick={(e) => { e.stopPropagation(); onEdit(card); }}
          >✏️</button>
          <button
            className="ib-card-action-btn delete"
            title="Delete"
            onClick={(e) => { e.stopPropagation(); onDelete(card.id); }}
          >🗑</button>
        </div>
      </div>
    </div>
  );
}

// ── Card Edit Modal ───────────────────────────
function CardModal({ card, columns, onSave, onClose }) {
  const [form, setForm] = useState({
    title:       card?.title       || "",
    description: card?.description || "",
    assetType:   card?.assetType   || "note",
    priority:    card?.priority    || "none",
    columnId:    card?.columnId    || columns[0]?.id || "",
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function handleSave() {
    if (!form.title.trim()) return;
    onSave({ ...card, ...form, title: form.title.trim(), updatedAt: Date.now() });
  }

  return (
    <div className="ib-modal-overlay" onClick={onClose}>
      <div className="ib-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ib-modal-header">
          <h3>{card?.id ? "Edit Card" : "New Research Card"}</h3>
          <button className="ib-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="ib-modal-body">
          {/* Title */}
          <div className="ib-field">
            <label>Title *</label>
            <input
              autoFocus
              placeholder="e.g. TCS — AI Division Thesis"
              value={form.title}
              onChange={e => set("title", e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSave()}
            />
          </div>

          {/* Description */}
          <div className="ib-field">
            <label>Research Notes</label>
            <textarea
              placeholder={"Supports Markdown:\n**bold**, *italic*\n- list item\n- [ ] checkbox\n- [x] done"}
              value={form.description}
              onChange={e => set("description", e.target.value)}
            />
            <span className="ib-md-hint">💡 Supports **bold**, *italic*, - lists, - [ ] checkboxes</span>
          </div>

          {/* Asset type + Column */}
          <div className="ib-field-row">
            <div className="ib-field">
              <label>Asset Type</label>
              <select value={form.assetType} onChange={e => set("assetType", e.target.value)}>
                {ASSET_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="ib-field">
              <label>Column</label>
              <select value={form.columnId} onChange={e => set("columnId", e.target.value)}>
                {columns.map(c => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Priority */}
          <div className="ib-field">
            <label>Sentiment / Priority</label>
            <div className="ib-priority-wrap">
              {PRIORITY_OPTS.map(p => (
                <div
                  key={p.value}
                  className={`ib-priority-opt ${form.priority === p.value ? p.cls : ""}`}
                  onClick={() => set("priority", p.value)}
                >
                  {p.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="ib-modal-footer">
          <button className="ib-btn ib-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="ib-btn ib-btn-primary" onClick={handleSave}>
            {card?.id ? "Save Changes" : "Add Card"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Column component ──────────────────────────
function BoardColumn({
  col, cards,
  onAddCard, onEditCard, onDeleteCard,
  onRenameCol, onDeleteCol,
  onDragStart, onDragEnd,
  onDragOver, onDrop,
  isDragOver,
}) {
  const [renaming, setRenaming]   = useState(false);
  const [nameVal, setNameVal]     = useState(col.title);
  const [menuOpen, setMenuOpen]   = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function commitRename() {
    if (nameVal.trim()) onRenameCol(col.id, nameVal.trim());
    setRenaming(false);
    setMenuOpen(false);
  }

  return (
    <div
      className={`ib-column ${isDragOver ? "drag-over" : ""}`}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, col.id)}
    >
      {/* Header */}
      <div className="ib-col-header">
        <div className="ib-col-dot" style={{ background: col.color }} />

        <div className="ib-col-title-wrap">
          {renaming ? (
            <input
              className="ib-col-title-input"
              value={nameVal}
              autoFocus
              onChange={e => setNameVal(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") { setRenaming(false); setNameVal(col.title); } }}
            />
          ) : (
            <div className="ib-col-title" title={col.title}>{col.title}</div>
          )}
        </div>

        <span className="ib-col-count">{cards.length}</span>

        <div className="ib-col-menu-wrap" ref={menuRef}>
          <button className="ib-col-menu-btn" onClick={() => setMenuOpen(o => !o)}>⋯</button>
          {menuOpen && (
            <div className="ib-col-menu-popup">
              <button className="ib-col-menu-item" onClick={() => { setRenaming(true); setMenuOpen(false); }}>
                ✏️ Rename
              </button>
              <button className="ib-col-menu-item" onClick={() => { onAddCard(col.id); setMenuOpen(false); }}>
                ➕ Add Card
              </button>
              <button className="ib-col-menu-item danger" onClick={() => { onDeleteCol(col.id); setMenuOpen(false); }}>
                🗑 Delete Column
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Cards */}
      <div className="ib-col-cards">
        {cards.length === 0 && (
          <div style={{ textAlign: "center", color: "var(--siq-muted)", fontSize: "0.75rem", padding: "24px 0", opacity: 0.5 }}>
            Drop cards here
          </div>
        )}
        {cards.map(card => (
          <BoardCard
            key={card.id}
            card={card}
            onEdit={onEditCard}
            onDelete={onDeleteCard}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="ib-col-footer">
        <button className="ib-quick-add-btn" onClick={() => onAddCard(col.id)}>
          ＋ Quick Add
        </button>
      </div>
    </div>
  );
}

// ── Main Board ────────────────────────────────
export default function IntelligenceBoard() {
  const [board, setBoard]             = useState(() => loadBoard());
  const [modal, setModal]             = useState(null);        // null | { card, defaultColId }
  const [filterType, setFilterType]   = useState("all");
  const [filterSent, setFilterSent]   = useState("all");
  const [searchQ, setSearchQ]         = useState("");
  const [dragCardId, setDragCardId]   = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);

  // Persist on every change
  useEffect(() => { saveBoard(board); }, [board]);

  // ── Derived: filtered cards per column ─────
  const getColCards = useCallback((colId) => {
    return Object.values(board.cards)
      .filter(c => {
        if (c.columnId !== colId) return false;
        if (filterType !== "all" && c.assetType !== filterType) return false;
        if (filterSent !== "all" && c.priority  !== filterSent) return false;
        if (searchQ) {
          const q = searchQ.toLowerCase();
          return (
            c.title.toLowerCase().includes(q) ||
            (c.description || "").toLowerCase().includes(q)
          );
        }
        return true;
      })
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }, [board.cards, filterType, filterSent, searchQ]);

  // ── Column actions ──────────────────────────
  function addColumn() {
    const col = {
      id:    uid(),
      title: "New Column",
      color: COL_COLORS[board.columns.length % COL_COLORS.length],
    };
    setBoard(b => ({ ...b, columns: [...b.columns, col] }));
  }

  function renameColumn(colId, title) {
    setBoard(b => ({
      ...b,
      columns: b.columns.map(c => c.id === colId ? { ...c, title } : c),
    }));
  }

  function deleteColumn(colId) {
    if (!window.confirm("Delete this column and all its cards?")) return;
    setBoard(b => {
      const cards = { ...b.cards };
      Object.keys(cards).forEach(k => { if (cards[k].columnId === colId) delete cards[k]; });
      return { columns: b.columns.filter(c => c.id !== colId), cards };
    });
  }

  // ── Card actions ────────────────────────────
  function openAddCard(colId) {
    setModal({ card: null, defaultColId: colId });
  }

  function openEditCard(card) {
    setModal({ card, defaultColId: card.columnId });
  }

  function saveCard(cardData) {
    const id = cardData.id || uid();
    setBoard(b => ({
      ...b,
      cards: { ...b.cards, [id]: { ...cardData, id } },
    }));
    setModal(null);
  }

  function deleteCard(cardId) {
    setBoard(b => {
      const cards = { ...b.cards };
      delete cards[cardId];
      return { ...b, cards };
    });
  }

  // ── Drag & Drop ─────────────────────────────
  function handleDragStart(e, cardId) {
    setDragCardId(cardId);
    e.dataTransfer.effectAllowed = "move";
  }

  function handleDragEnd() {
    setDragCardId(null);
    setDragOverColId(null);
  }

  function handleDragOver(e, colId) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColId(colId);
  }

  function handleDrop(e, colId) {
    e.preventDefault();
    if (!dragCardId) return;
    setBoard(b => ({
      ...b,
      cards: {
        ...b.cards,
        [dragCardId]: { ...b.cards[dragCardId], columnId: colId, updatedAt: Date.now() },
      },
    }));
    setDragCardId(null);
    setDragOverColId(null);
  }

  // ── Filter counts ───────────────────────────
  const totalCards = Object.keys(board.cards).length;

  return (
    <div className="ib-page">
      {/* Top Bar */}
      <div className="ib-topbar">
        <div className="ib-topbar-title">
          <span className="ib-icon">📋</span>
          Intel Board
          <span style={{ fontWeight: 400, color: "var(--siq-muted)", fontSize: "0.78rem" }}>
            — {totalCards} notes
          </span>
        </div>

        <div className="ib-topbar-divider" />

        {/* Asset type filters */}
        <div className="ib-filter-group">
          <span className="ib-filter-label">Type:</span>
          {[
            { v: "all",    l: "All"    },
            { v: "stock",  l: "Stock"  },
            { v: "sector", l: "Sector" },
            { v: "mf",     l: "MF"     },
            { v: "etf",    l: "ETF"    },
            { v: "bond",   l: "Bond"   },
            { v: "note",   l: "Note"   },
          ].map(f => (
            <button
              key={f.v}
              className={`ib-filter-btn ${filterType === f.v ? "active" : ""}`}
              onClick={() => setFilterType(f.v)}
            >{f.l}</button>
          ))}

          <div className="ib-topbar-divider" />

          <span className="ib-filter-label">Sentiment:</span>
          {[
            { v: "all",  l: "All",     cls: "" },
            { v: "bull", l: "🟢 Bull",  cls: "sentiment-bull" },
            { v: "bear", l: "🔴 Bear",  cls: "sentiment-bear" },
            { v: "neut", l: "🟡 Neut",  cls: "sentiment-neut" },
          ].map(f => (
            <button
              key={f.v}
              className={`ib-filter-btn ${f.cls} ${filterSent === f.v ? "active" : ""}`}
              onClick={() => setFilterSent(f.v)}
            >{f.l}</button>
          ))}
        </div>

        {/* Search */}
        <div className="ib-search-wrap">
          <span className="ib-search-icon">🔍</span>
          <input
            placeholder="Search notes…"
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
          />
        </div>

        {/* Add column */}
        <button className="ib-add-col-btn" onClick={addColumn}>
          ＋ Add Column
        </button>
      </div>

      {/* Board */}
      {board.columns.length === 0 ? (
        <div className="ib-empty-board">
          <div className="ib-empty-icon">📋</div>
          <p>Your Intel Board is empty. Add a column to start organizing your research.</p>
          <button className="ib-btn ib-btn-primary" style={{ marginTop: 8 }} onClick={addColumn}>
            ＋ Add First Column
          </button>
        </div>
      ) : (
        <div className="ib-board">
          {board.columns.map(col => (
            <BoardColumn
              key={col.id}
              col={col}
              cards={getColCards(col.id)}
              onAddCard={openAddCard}
              onEditCard={openEditCard}
              onDeleteCard={deleteCard}
              onRenameCol={renameColumn}
              onDeleteCol={deleteColumn}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, col.id)}
              onDrop={handleDrop}
              isDragOver={dragOverColId === col.id}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      {modal !== null && (
        <CardModal
          card={modal.card ? { ...modal.card } : { columnId: modal.defaultColId }}
          columns={board.columns}
          onSave={saveCard}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}